import { useState, useEffect } from 'react'
import {
  Box, Text, Table, Badge, Button, Modal, TextInput, Select, Stack, Group,
  ActionIcon, Tooltip, CopyButton, Alert, Loader, Center, SimpleGrid, Menu, Textarea, PasswordInput, Divider,
} from '@mantine/core'
import {
  IconShieldLock, IconPlus, IconCopy, IconCheck, IconDotsVertical,
  IconPower, IconChartBar, IconLogout, IconBuildingSkyscraper,
  IconExternalLink, IconShare, IconKey, IconPencil, IconTrash, IconAlertTriangle,
  IconFileText, IconPlugConnected, IconSettings,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { usePlatformAuth } from '../../hooks/usePlatformAuth'
import {
  listTenants, createTenant, setTenantActive, getTenantStats, updateTenantPlan,
  buildTenantUrl, resetAdminPassword, updateTenantName, deleteTenant, updateTenantContractDetails,
  updateTenantIntegrationSettings, getPlatformEmailSettings, PlatformEmailSettings,
} from '../../api/platformClient'
import WebsiteAnalyticsCard from './WebsiteAnalyticsCard'

interface Tenant {
  id: number
  subdomain: string
  schemaName: string
  displayName: string
  isActive: boolean
  plan?: string
  goLiveDate?: string
  contractStartDate?: string
  contractExpiryDate?: string
  notificationEmails?: string
  cloudinaryCloudName?: string
  emailFromName?: string
  emailFromAddress?: string
  hasCloudinary: boolean
  hasLine: boolean
  hasEmailFrom: boolean
  createdAt: string
}

// dates จาก backend เป็น string รูปแบบ YYYY-MM-DD ตรงๆ (Postgres date column) — format ให้อ่านง่าย
// ในตาราง โดยไม่ต้องพึ่ง timezone conversion ใดๆ (new Date('YYYY-MM-DD') ตีความเป็น UTC เที่ยงคืน
// ซึ่งปลอดภัยกับการ "แค่แสดงวันที่" แบบนี้ ไม่เกิดปัญหาเลื่อนวันข้ามโซนเวลา)
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'

const CARD_BG = '#131a2c'
const CARD_BORDER = '1px solid #232c44'
const LABEL_COLOR = '#9aa3ba'

export default function PlatformDashboardPage() {
  const { username, doLogout } = usePlatformAuth()
  const navigate = useNavigate()

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createdResult, setCreatedResult] = useState<{ subdomain: string; username: string; password: string } | null>(null)

  const [form, setForm] = useState({ subdomain: '', displayName: '', adminEmail: '', plan: 'starter' })

  const [statsTenant, setStatsTenant] = useState<Tenant | null>(null)
  const [resetResult, setResetResult] = useState<{ tenant: Tenant; username: string; email: string; password: string; emailSent: boolean } | null>(null)
  const [resettingId, setResettingId] = useState<number | null>(null)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [editNameValue, setEditNameValue] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [deletingTenant, setDeletingTenant] = useState<Tenant | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [editingContract, setEditingContract] = useState<Tenant | null>(null)
  const [contractForm, setContractForm] = useState({
    plan: '', goLiveDate: '', contractStartDate: '', contractExpiryDate: '', notificationEmails: '',
  })
  const [savingContract, setSavingContract] = useState(false)
  const [editingIntegrations, setEditingIntegrations] = useState<Tenant | null>(null)
  const [integrationsForm, setIntegrationsForm] = useState({
    cloudinaryCloudName: '', cloudinaryApiKey: '', cloudinaryApiSecret: '',
    lineChannelSecret: '', lineChannelAccessToken: '',
    emailFromName: '', emailFromAddress: '',
  })
  const [savingIntegrations, setSavingIntegrations] = useState(false)
  const [stats, setStats] = useState<any>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [emailStatus, setEmailStatus] = useState<PlatformEmailSettings | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await listTenants()
      setTenants(res.data)
    } catch {
      notifications.show({ color: 'red', message: 'Failed to load tenants' })
    } finally {
      setLoading(false)
    }
  }

  // ── สถานะ SMTP ของระบบ — ดึงมาโชว์เป็น banner บนสุดของหน้าหลัก ไม่ให้ error กระทบหน้าทั้งหน้า
  // ถ้าเรียกไม่สำเร็จ (เช่น token หมดอายุพอดี) แค่ไม่โชว์ banner เฉยๆ ──
  const loadEmailStatus = async () => {
    try {
      const { data } = await getPlatformEmailSettings()
      setEmailStatus(data)
    } catch {
      setEmailStatus(null)
    }
  }

  useEffect(() => { load(); loadEmailStatus() }, [])

  const handleLogout = () => { doLogout(); navigate('/platform/login', { replace: true }) }

  const handleCreate = async () => {
    if (!form.subdomain || !form.displayName || !form.adminEmail) {
      notifications.show({ color: 'orange', message: 'Please fill in every field' })
      return
    }
    setCreating(true)
    try {
      const res = await createTenant(form)
      setCreatedResult({
        subdomain: res.data.tenant.subdomain,
        username: res.data.initialAdmin.username,
        password: res.data.initialAdmin.temporaryPassword,
      })
      setForm({ subdomain: '', displayName: '', adminEmail: '', plan: 'starter' })
      await load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not create tenant' })
    } finally {
      setCreating(false)
    }
  }

  const closeCreateModal = () => { setCreateOpen(false); setCreatedResult(null) }

  const handleToggleActive = async (tenant: Tenant) => {
    try {
      await setTenantActive(tenant.id, !tenant.isActive)
      notifications.show({
        color: 'teal',
        message: `${tenant.displayName} is now ${!tenant.isActive ? 'active' : 'suspended'}`,
      })
      await load()
    } catch {
      notifications.show({ color: 'red', message: 'Could not update tenant status' })
    }
  }

  const handleResetPassword = async (tenant: Tenant) => {
    if (!window.confirm(`Reset the admin password for ${tenant.displayName}? Their current password will stop working immediately.`)) return
    setResettingId(tenant.id)
    try {
      const res = await resetAdminPassword(tenant.id)
      setResetResult({
        tenant, username: res.data.username, email: res.data.email,
        password: res.data.temporaryPassword, emailSent: res.data.emailSent,
      })
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not reset password' })
    } finally {
      setResettingId(null)
    }
  }

  const openEditName = (tenant: Tenant) => {
    setEditingTenant(tenant)
    setEditNameValue(tenant.displayName)
  }

  const handleSaveName = async () => {
    if (!editingTenant || !editNameValue.trim()) return
    setSavingName(true)
    try {
      await updateTenantName(editingTenant.id, editNameValue.trim())
      notifications.show({ color: 'teal', message: 'Company name updated' })
      setEditingTenant(null)
      await load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not update name' })
    } finally {
      setSavingName(false)
    }
  }

  const openDeleteConfirm = (tenant: Tenant) => {
    setDeletingTenant(tenant)
    setDeleteConfirmText('')
  }

  const handleDelete = async () => {
    if (!deletingTenant || deleteConfirmText !== deletingTenant.subdomain) return
    setDeleting(true)
    try {
      await deleteTenant(deletingTenant.id)
      notifications.show({ color: 'teal', message: `${deletingTenant.displayName} has been permanently deleted` })
      setDeletingTenant(null)
      await load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not delete tenant' })
    } finally {
      setDeleting(false)
    }
  }

  const openEditContract = (tenant: Tenant) => {
    setEditingContract(tenant)
    setContractForm({
      plan: tenant.plan ?? '',
      goLiveDate: tenant.goLiveDate ?? '',
      contractStartDate: tenant.contractStartDate ?? '',
      contractExpiryDate: tenant.contractExpiryDate ?? '',
      notificationEmails: tenant.notificationEmails ?? '',
    })
  }

  const handleSaveContract = async () => {
    if (!editingContract) return
    setSavingContract(true)
    try {
      await updateTenantContractDetails(editingContract.id, {
        plan: contractForm.plan || undefined,
        goLiveDate: contractForm.goLiveDate || null,
        contractStartDate: contractForm.contractStartDate || null,
        contractExpiryDate: contractForm.contractExpiryDate || null,
        notificationEmails: contractForm.notificationEmails || null,
      })
      notifications.show({ color: 'teal', message: 'Contract details updated' })
      setEditingContract(null)
      await load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not update contract details' })
    } finally {
      setSavingContract(false)
    }
  }

  const openEditIntegrations = (tenant: Tenant) => {
    setEditingIntegrations(tenant)
    setIntegrationsForm({
      // cloudinaryCloudName/emailFromName/emailFromAddress ไม่ใช่ secret จริง แสดงค่าปัจจุบันได้ตรงๆ
      cloudinaryCloudName: tenant.cloudinaryCloudName ?? '',
      emailFromName: tenant.emailFromName ?? '',
      emailFromAddress: tenant.emailFromAddress ?? '',
      // secret จริง (API secret, channel secret, access token) ไม่เคยถูกส่งกลับมาจาก backend
      // เลย — เริ่มว่างเสมอ พิมพ์ใหม่ถึงจะเปลี่ยน เว้นว่างไว้ = ไม่แตะค่าเดิม (เหมือน pattern
      // "เว้นว่างถ้าไม่เปลี่ยนรหัสผ่าน" ที่ใช้อยู่แล้วตอนแก้ไข user)
      cloudinaryApiKey: '',
      cloudinaryApiSecret: '',
      lineChannelSecret: '',
      lineChannelAccessToken: '',
    })
  }

  const handleSaveIntegrations = async () => {
    if (!editingIntegrations) return
    setSavingIntegrations(true)
    try {
      await updateTenantIntegrationSettings(editingIntegrations.id, {
        cloudinaryCloudName: integrationsForm.cloudinaryCloudName || null,
        emailFromName: integrationsForm.emailFromName || null,
        emailFromAddress: integrationsForm.emailFromAddress || null,
        // secret จริง — เว้นว่างไว้ = ไม่แตะเลย (undefined) ไม่ใช่ "ล้างค่า" (null) เพราะช่องพวกนี้
        // เริ่มว่างเสมอไม่ว่าจะเคยตั้งค่าไว้หรือยัง ถ้าส่ง null ไปตอนเว้นว่างจะเผลอลบ secret เดิม
        // ทิ้งไปแค่เพราะเปิด modal ขึ้นมาดูแล้วกด Save โดยไม่ได้พิมพ์อะไรใหม่เลย
        cloudinaryApiKey: integrationsForm.cloudinaryApiKey || undefined,
        cloudinaryApiSecret: integrationsForm.cloudinaryApiSecret || undefined,
        lineChannelSecret: integrationsForm.lineChannelSecret || undefined,
        lineChannelAccessToken: integrationsForm.lineChannelAccessToken || undefined,
      })
      notifications.show({ color: 'teal', message: 'Integration settings updated' })
      setEditingIntegrations(null)
      await load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Could not update integration settings' })
    } finally {
      setSavingIntegrations(false)
    }
  }

  const openStats = async (tenant: Tenant) => {
    setStatsTenant(tenant)
    setStatsLoading(true)
    try {
      const res = await getTenantStats(tenant.id)
      setStats(res.data)
    } catch {
      notifications.show({ color: 'red', message: 'Could not load tenant stats' })
    } finally {
      setStatsLoading(false)
    }
  }

  const activeCount = tenants.filter(t => t.isActive).length

  return (
    <Box style={{ minHeight: '100vh', background: '#0a0e17', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <Box style={{ borderBottom: CARD_BORDER, padding: '18px 32px' }}>
        <Group justify="space-between">
          <Group gap={10}>
            <Box style={{
              width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg, #f5a623, #d98c1a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconShieldLock size={18} color="#0a0e17" />
            </Box>
            <Text style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Platform Console</Text>
          </Group>
          <Group gap={14}>
            <Button
              leftSection={<IconSettings size={15} />}
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => navigate('/platform/settings')}
            >
              Settings
            </Button>
            <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>{username}</Text>
            <Tooltip label="Sign out">
              <ActionIcon variant="subtle" color="gray" onClick={handleLogout}>
                <IconLogout size={17} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      <Box style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
        {/* ── สถานะการตั้งค่าอีเมล — โชว์บนสุดของหน้าหลักหลัง login ──────────────── */}
        {emailStatus && (
          <Box
            mb={20}
            style={{
              background: emailStatus.smtpHost && emailStatus.smtpUser && emailStatus.hasPassword
                ? 'rgba(61, 220, 151, 0.08)' : 'rgba(245, 166, 35, 0.08)',
              border: `1px solid ${emailStatus.smtpHost && emailStatus.smtpUser && emailStatus.hasPassword ? '#1f4d3a' : '#4d3a1f'}`,
              borderRadius: 10, padding: '12px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
            }}
          >
            <Group gap={10}>
              {emailStatus.smtpHost && emailStatus.smtpUser && emailStatus.hasPassword ? (
                <IconCheck size={16} color="#3ddc97" />
              ) : (
                <IconAlertTriangle size={16} color="#f5a623" />
              )}
              <Text style={{ fontSize: 13, color: '#e5e7eb' }}>
                {emailStatus.smtpHost && emailStatus.smtpUser && emailStatus.hasPassword
                  ? <>Platform email is configured — sending as <strong>{emailStatus.fromAddress || emailStatus.smtpUser}</strong> via <strong>{emailStatus.smtpHost}</strong>.</>
                  : 'Platform email (SMTP) is not fully configured yet — demo requests from the sales site may not be delivered.'}
              </Text>
            </Group>
            <Button size="xs" variant="subtle" color="gray" onClick={() => navigate('/platform/settings')}>
              {emailStatus.smtpHost && emailStatus.smtpUser && emailStatus.hasPassword ? 'Manage' : 'Set up now'}
            </Button>
          </Box>
        )}

        {/* ── Website Analytics — ย้ายมาจากหน้า Settings ให้เห็นทันทีตั้งแต่เข้าหน้าหลัก ── */}
        <WebsiteAnalyticsCard />

        {/* Summary cards */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} mb={28}>
          {[
            { label: 'Total tenants', value: tenants.length, color: '#fff' },
            { label: 'Active', value: activeCount, color: '#3ddc97' },
            { label: 'Suspended', value: tenants.length - activeCount, color: '#f5a623' },
          ].map(c => (
            <Box key={c.label} style={{ background: CARD_BG, border: CARD_BORDER, borderRadius: 12, padding: '16px 20px' }}>
              <Text style={{ color: LABEL_COLOR, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                {c.label}
              </Text>
              <Text style={{ color: c.color, fontSize: 28, fontWeight: 800, marginTop: 4 }}>{c.value}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* Tenant list */}
        <Box style={{ background: CARD_BG, border: CARD_BORDER, borderRadius: 12, overflow: 'hidden' }}>
          <Group justify="space-between" p={18} style={{ borderBottom: CARD_BORDER }}>
            <Text style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Tenants</Text>
            <Button
              leftSection={<IconPlus size={15} />}
              size="xs"
              onClick={() => setCreateOpen(true)}
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
            >
              New tenant
            </Button>
          </Group>

          {loading ? (
            <Center p={40}><Loader color="orange" /></Center>
          ) : tenants.length === 0 ? (
            <Center p={40}>
              <Stack align="center" gap={6}>
                <IconBuildingSkyscraper size={32} color={LABEL_COLOR} />
                <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>No tenants yet — create the first one above.</Text>
              </Stack>
            </Center>
          ) : (
            <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr style={{ background: '#0e1420' }}>
                  {['Subdomain', 'Company', 'Plan', 'Go-live', 'Contract start', 'Contract expiry', 'Integrations', 'Status', 'Created', ''].map(h => (
                    <Table.Th key={h} style={{ color: LABEL_COLOR, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', border: 'none' }}>
                      {h}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tenants.map(t => (
                  <Table.Tr key={t.id} style={{ borderBottom: CARD_BORDER }}>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: '#f5a623', fontSize: 13, fontFamily: 'monospace' }}>{t.subdomain}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: '#fff', fontSize: 13 }}>{t.displayName}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>{t.plan ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>{fmtDate(t.goLiveDate)}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>{fmtDate(t.contractStartDate)}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>{fmtDate(t.contractExpiryDate)}</Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Group gap={6}>
                        <Tooltip label={`Cloudinary: ${t.hasCloudinary ? 'configured' : 'using default'}`}>
                          <Badge size="xs" circle color={t.hasCloudinary ? 'teal' : 'gray'} variant={t.hasCloudinary ? 'filled' : 'outline'} />
                        </Tooltip>
                        <Tooltip label={`LINE: ${t.hasLine ? 'configured' : 'using default'}`}>
                          <Badge size="xs" circle color={t.hasLine ? 'teal' : 'gray'} variant={t.hasLine ? 'filled' : 'outline'} />
                        </Tooltip>
                        <Tooltip label={`Email sender identity: ${t.hasEmailFrom ? 'configured' : 'using default'}`}>
                          <Badge size="xs" circle color={t.hasEmailFrom ? 'teal' : 'gray'} variant={t.hasEmailFrom ? 'filled' : 'outline'} />
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Badge color={t.isActive ? 'teal' : 'orange'} variant="light" size="sm">
                        {t.isActive ? 'Active' : 'Suspended'}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ border: 'none' }}>
                      <Text style={{ color: LABEL_COLOR, fontSize: 12 }}>
                        {new Date(t.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ border: 'none', textAlign: 'right' }}>
                      <Menu shadow="md" width={190} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray"><IconDotsVertical size={16} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconChartBar size={14} />} onClick={() => openStats(t)}>
                            View usage
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconCopy size={14} />}
                            onClick={() => {
                              const link = buildTenantUrl(t.subdomain)
                              if (!link) {
                                notifications.show({ color: 'orange', message: 'VITE_TENANT_BASE_DOMAIN is not configured' })
                                return
                              }
                              navigator.clipboard.writeText(link)
                              notifications.show({ color: 'teal', message: 'Link copied' })
                            }}
                          >
                            Copy login link
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconPencil size={14} />}
                            onClick={() => openEditName(t)}
                          >
                            Edit company name
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconFileText size={14} />}
                            onClick={() => openEditContract(t)}
                          >
                            Edit plan &amp; contract details
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconPlugConnected size={14} />}
                            onClick={() => openEditIntegrations(t)}
                          >
                            Edit integrations
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconKey size={14} />}
                            disabled={resettingId === t.id}
                            onClick={() => handleResetPassword(t)}
                          >
                            Reset admin password
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconPower size={14} />}
                            color={t.isActive ? 'red' : 'teal'}
                            onClick={() => handleToggleActive(t)}
                          >
                            {t.isActive ? 'Suspend tenant' : 'Reactivate tenant'}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => openDeleteConfirm(t)}
                          >
                            Delete tenant permanently
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>
          )}
        </Box>
      </Box>

      {/* Create tenant modal */}
      <Modal
        opened={createOpen}
        onClose={closeCreateModal}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>{createdResult ? 'Tenant created' : 'Create a new tenant'}</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        {createdResult ? (
          <Stack gap={14}>
            <Alert color="teal" variant="light" radius="md">
              <Text size="sm">
                <strong>{createdResult.subdomain}</strong> is ready. Share the link and credentials below with
                whoever's setting up this company — the temporary password will not be shown again.
              </Text>
            </Alert>
            {(() => {
              const link = buildTenantUrl(createdResult.subdomain)
              if (!link) {
                return (
                  <Alert color="orange" variant="light" radius="md">
                    <Text size="sm">
                      VITE_TENANT_BASE_DOMAIN isn't configured, so a link can't be shown — set it in this
                      deployment's environment variables to enable this.
                    </Text>
                  </Alert>
                )
              }
              return (
                <Box style={{ background: '#0e1420', border: CARD_BORDER, borderRadius: 10, padding: 14 }}>
                  <Text style={{ color: LABEL_COLOR, fontSize: 11, marginBottom: 2 }}>Login link</Text>
                  <Group justify="space-between" wrap="nowrap">
                    <Text style={{ color: '#fff', fontSize: 13, wordBreak: 'break-all' }}>{link}</Text>
                    <Group gap={4} wrap="nowrap">
                      <CopyButton value={link}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied' : 'Copy link'}>
                            <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      {!!navigator.share && (
                        <Tooltip label="Share">
                          <ActionIcon
                            size="sm" variant="subtle" color="gray"
                            onClick={() => navigator.share({ title: createdResult.subdomain, url: link }).catch(() => {})}
                          >
                            <IconShare size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Open">
                        <ActionIcon size="sm" variant="subtle" color="gray" component="a" href={link} target="_blank" rel="noreferrer">
                          <IconExternalLink size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Box>
              )
            })()}
            <Box style={{ background: '#0e1420', border: CARD_BORDER, borderRadius: 10, padding: 14 }}>
              <Text style={{ color: LABEL_COLOR, fontSize: 11, marginBottom: 2 }}>Username</Text>
              <Text style={{ color: '#fff', fontFamily: 'monospace', fontSize: 14, marginBottom: 10 }}>{createdResult.username}</Text>
              <Text style={{ color: LABEL_COLOR, fontSize: 11, marginBottom: 2 }}>Temporary password</Text>
              <Group gap={8}>
                <Text style={{ color: '#f5a623', fontFamily: 'monospace', fontSize: 14 }}>{createdResult.password}</Text>
                <CopyButton value={createdResult.password}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'}>
                      <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Box>
            <Button onClick={closeCreateModal} fullWidth
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}>
              Done
            </Button>
          </Stack>
        ) : (
          <Stack gap={14}>
            <TextInput
              label="Subdomain" placeholder="acme" required
              description="Becomes acme.assetsservice.com — lowercase letters, numbers, hyphens only"
              value={form.subdomain}
              onChange={(e) => setForm(p => ({ ...p, subdomain: e.target.value.toLowerCase() }))}
              styles={{ label: { color: LABEL_COLOR }, description: { color: '#4b5468' }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <TextInput
              label="Company name" placeholder="Acme Corp" required
              value={form.displayName}
              onChange={(e) => setForm(p => ({ ...p, displayName: e.target.value }))}
              styles={{ label: { color: LABEL_COLOR }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <TextInput
              label="Admin email" placeholder="admin@acme.com" required type="email"
              description="The company's first admin account is created automatically with this email"
              value={form.adminEmail}
              onChange={(e) => setForm(p => ({ ...p, adminEmail: e.target.value }))}
              styles={{ label: { color: LABEL_COLOR }, description: { color: '#4b5468' }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <Select
              label="Plan"
              data={[{ value: 'starter', label: 'Starter' }, { value: 'growth', label: 'Growth' }, { value: 'enterprise', label: 'Enterprise' }]}
              value={form.plan}
              onChange={(v) => setForm(p => ({ ...p, plan: v ?? 'starter' }))}
              styles={{ label: { color: LABEL_COLOR }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <Button onClick={handleCreate} loading={creating} fullWidth mt={6}
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}>
              Create tenant
            </Button>
          </Stack>
        )}
      </Modal>

      {/* Reset password result modal */}
      <Modal
        opened={!!resetResult}
        onClose={() => setResetResult(null)}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>Password reset</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        {resetResult && (
          <Stack gap={14}>
            <Alert color={resetResult.emailSent ? 'teal' : 'orange'} variant="light" radius="md">
              <Text size="sm">
                {resetResult.emailSent
                  ? <>Emailed the new password to <strong>{resetResult.email}</strong>. It's also shown below in case the email doesn't arrive — it will not be shown again after you close this.</>
                  : <>Could not send an email (no address on file, or email isn't configured on this deployment) — share this password with {resetResult.tenant.displayName}'s admin directly. It will not be shown again.</>
                }
              </Text>
            </Alert>
            <Box style={{ background: '#0e1420', border: CARD_BORDER, borderRadius: 10, padding: 14 }}>
              <Text style={{ color: LABEL_COLOR, fontSize: 11, marginBottom: 2 }}>Username</Text>
              <Text style={{ color: '#fff', fontFamily: 'monospace', fontSize: 14, marginBottom: 10 }}>{resetResult.username}</Text>
              <Text style={{ color: LABEL_COLOR, fontSize: 11, marginBottom: 2 }}>New temporary password</Text>
              <Group gap={8}>
                <Text style={{ color: '#f5a623', fontFamily: 'monospace', fontSize: 14 }}>{resetResult.password}</Text>
                <CopyButton value={resetResult.password}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'}>
                      <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Box>
            <Button onClick={() => setResetResult(null)} fullWidth
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}>
              Done
            </Button>
          </Stack>
        )}
      </Modal>

      {/* Edit company name modal */}
      <Modal
        opened={!!editingTenant}
        onClose={() => setEditingTenant(null)}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>Edit company name</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        <Stack gap={14}>
          <Text size="sm" style={{ color: LABEL_COLOR }}>
            Updates the name shown here and inside {editingTenant?.subdomain}'s own app.
          </Text>
          <TextInput
            label="Company name"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            styles={{ label: { color: LABEL_COLOR }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
          />
          <Group justify="flex-end" gap={8}>
            <Button variant="subtle" color="gray" onClick={() => setEditingTenant(null)}>Cancel</Button>
            <Button
              loading={savingName}
              disabled={!editNameValue.trim()}
              onClick={handleSaveName}
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit plan & contract details modal */}
      <Modal
        opened={!!editingContract}
        onClose={() => setEditingContract(null)}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>Plan &amp; contract details</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        <Stack gap={14}>
          <Select
            label="Plan"
            placeholder="No plan set"
            data={[{ value: 'starter', label: 'Starter' }, { value: 'growth', label: 'Growth' }, { value: 'enterprise', label: 'Enterprise' }]}
            value={contractForm.plan || null}
            onChange={(v) => setContractForm(p => ({ ...p, plan: v ?? '' }))}
            clearable
            styles={{ label: { color: LABEL_COLOR }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
          />
          <SimpleGrid cols={3}>
            <TextInput
              label="Go-live date" type="date"
              value={contractForm.goLiveDate}
              onChange={(e) => setContractForm(p => ({ ...p, goLiveDate: e.target.value }))}
              styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <TextInput
              label="Contract start" type="date"
              value={contractForm.contractStartDate}
              onChange={(e) => setContractForm(p => ({ ...p, contractStartDate: e.target.value }))}
              styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
            <TextInput
              label="Contract expiry" type="date"
              value={contractForm.contractExpiryDate}
              onChange={(e) => setContractForm(p => ({ ...p, contractExpiryDate: e.target.value }))}
              styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
            />
          </SimpleGrid>
          <Textarea
            label="Notification emails"
            description="Comma-separated. Gets reminded 30, 14, 7, and 1 day before the contract expiry date above."
            placeholder="sales@yourcompany.com, ops@yourcompany.com"
            minRows={2}
            value={contractForm.notificationEmails}
            onChange={(e) => setContractForm(p => ({ ...p, notificationEmails: e.target.value }))}
            styles={{ label: { color: LABEL_COLOR }, description: { color: '#4b5468', fontSize: 11 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
          />
          <Group justify="flex-end" gap={8}>
            <Button variant="subtle" color="gray" onClick={() => setEditingContract(null)}>Cancel</Button>
            <Button
              loading={savingContract}
              onClick={handleSaveContract}
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit integrations modal */}
      <Modal
        opened={!!editingIntegrations}
        onClose={() => setEditingIntegrations(null)}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>Integration settings</Text>}
        centered
        radius="md"
        size="lg"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        <Stack gap={16}>
          <Text size="xs" style={{ color: LABEL_COLOR }}>
            Leave any field blank to fall back to this deployment's own default. Fill in every field in a
            section for that section's override to take effect — a partially-filled section is ignored.
          </Text>

          <Box>
            <Text size="sm" fw={600} style={{ color: '#fff' }} mb={6}>Cloudinary</Text>
            <Stack gap={8}>
              <TextInput
                label="Cloud name" value={integrationsForm.cloudinaryCloudName}
                onChange={(e) => setIntegrationsForm(p => ({ ...p, cloudinaryCloudName: e.target.value }))}
                styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
              />
              <SimpleGrid cols={2}>
                <TextInput
                  label="API key" value={integrationsForm.cloudinaryApiKey}
                  placeholder={editingIntegrations?.hasCloudinary ? 'Already set — leave blank to keep' : ''}
                  onChange={(e) => setIntegrationsForm(p => ({ ...p, cloudinaryApiKey: e.target.value }))}
                  styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
                />
                <PasswordInput
                  label="API secret" value={integrationsForm.cloudinaryApiSecret}
                  placeholder={editingIntegrations?.hasCloudinary ? 'Already set — leave blank to keep' : ''}
                  onChange={(e) => setIntegrationsForm(p => ({ ...p, cloudinaryApiSecret: e.target.value }))}
                  styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
                />
              </SimpleGrid>
            </Stack>
          </Box>

          <Divider color={CARD_BORDER} />

          <Box>
            <Text size="sm" fw={600} style={{ color: '#fff' }} mb={6}>LINE</Text>
            <Text size="xs" style={{ color: '#4b5468' }} mb={8}>
              Once set, point that tenant's own LINE Official Account webhook at{' '}
              <Text component="span" style={{ color: LABEL_COLOR }}>
                {import.meta.env.VITE_API_URL}/line/webhook/{editingIntegrations?.subdomain}
              </Text>
              {' '}instead of the shared default webhook URL.
            </Text>
            <Stack gap={8}>
              <PasswordInput
                label="Channel secret" value={integrationsForm.lineChannelSecret}
                placeholder={editingIntegrations?.hasLine ? 'Already set — leave blank to keep' : ''}
                onChange={(e) => setIntegrationsForm(p => ({ ...p, lineChannelSecret: e.target.value }))}
                styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
              />
              <PasswordInput
                label="Channel access token" value={integrationsForm.lineChannelAccessToken}
                placeholder={editingIntegrations?.hasLine ? 'Already set — leave blank to keep' : ''}
                onChange={(e) => setIntegrationsForm(p => ({ ...p, lineChannelAccessToken: e.target.value }))}
                styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
              />
            </Stack>
          </Box>

          <Divider color={CARD_BORDER} />

          <Box>
            <Text size="sm" fw={600} style={{ color: '#fff' }} mb={6}>Email</Text>
            <Text size="xs" style={{ color: '#4b5468' }} mb={8}>
              Changes only who the recipient sees as the sender — this deployment's own email
              provider is still what actually sends it.
            </Text>
            <SimpleGrid cols={2}>
              <TextInput
                label="From name" placeholder="Acme Corp"
                value={integrationsForm.emailFromName}
                onChange={(e) => setIntegrationsForm(p => ({ ...p, emailFromName: e.target.value }))}
                styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
              />
              <TextInput
                label="From address" placeholder="noreply@acme.com"
                value={integrationsForm.emailFromAddress}
                onChange={(e) => setIntegrationsForm(p => ({ ...p, emailFromAddress: e.target.value }))}
                styles={{ label: { color: LABEL_COLOR, fontSize: 12 }, input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
              />
            </SimpleGrid>
          </Box>

          <Group justify="flex-end" gap={8}>
            <Button variant="subtle" color="gray" onClick={() => setEditingIntegrations(null)}>Cancel</Button>
            <Button
              loading={savingIntegrations}
              onClick={handleSaveIntegrations}
              styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete tenant confirmation modal — requires typing the subdomain to confirm, since this
          permanently drops the tenant's entire database schema with no way to undo it */}
      <Modal
        opened={!!deletingTenant}
        onClose={() => setDeletingTenant(null)}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>Delete tenant permanently</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        <Stack gap={14}>
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" radius="md">
            <Text size="sm">
              This permanently deletes <strong>{deletingTenant?.displayName}</strong> and every record it has —
              assets, users, licenses, everything. This cannot be undone.
            </Text>
          </Alert>
          <Text size="sm" style={{ color: LABEL_COLOR }}>
            Type <strong style={{ color: '#fff' }}>{deletingTenant?.subdomain}</strong> to confirm.
          </Text>
          <TextInput
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deletingTenant?.subdomain}
            styles={{ input: { background: '#0e1420', border: CARD_BORDER, color: '#fff' } }}
          />
          <Group justify="flex-end" gap={8}>
            <Button variant="subtle" color="gray" onClick={() => setDeletingTenant(null)}>Cancel</Button>
            <Button
              color="red"
              loading={deleting}
              disabled={deleteConfirmText !== deletingTenant?.subdomain}
              onClick={handleDelete}
            >
              Delete permanently
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Stats modal */}
      <Modal
        opened={!!statsTenant}
        onClose={() => { setStatsTenant(null); setStats(null) }}
        title={<Text style={{ color: '#fff', fontWeight: 700 }}>{statsTenant?.displayName}</Text>}
        centered
        radius="md"
        styles={{ content: { background: CARD_BG }, header: { background: CARD_BG }, close: { color: LABEL_COLOR } }}
      >
        {statsLoading ? (
          <Center p={30}><Loader color="orange" /></Center>
        ) : stats ? (
          <SimpleGrid cols={3}>
            {[
              { label: 'Users', value: stats.userCount },
              { label: 'Assets', value: stats.assetCount },
              { label: 'Last login', value: stats.lastLogin ? new Date(stats.lastLogin).toLocaleDateString('en-GB') : '—' },
            ].map(s => (
              <Box key={s.label} style={{ background: '#0e1420', border: CARD_BORDER, borderRadius: 10, padding: 14, textAlign: 'center' }}>
                <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>{s.label}</Text>
                <Text style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginTop: 4 }}>{s.value}</Text>
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </Modal>
    </Box>
  )
}
